import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FirstStep from './components/FirstStep';
import SecondStep from './components/SecondStep';
import { useTranslation } from 'react-i18next';
import {
  ASKING_PRICE_SELECT,
  CREATE_LISTING_OPTIONS,
  DEAL_OUTCOME,
  DEAL_ROOM_TOTAL_PACKAGE_GST,
  PublishDealRoomValues,
  REVENUE_DESCRIPTION,
  STOCK_AT_VALUE,
} from 'models/DealRoom';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-final-form';
import utils, {
  getCreateListingRoute,
  getDataRoomRoute,
  getSellingPortfolio,
  getShouldHasRevenueShares,
  getUserFullName,
  sentryError,
  shouldShowFrachisorFee,
} from 'utils';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useQueryParams from 'hooks/useQueryParams';
import { YesNo } from 'models/Fields';
import { FormWrapper } from 'components/FormWrapper';
import arrayMutators from 'final-form-arrays';
import { LISTING_GOING_CONCERN, LISTING_URGENT } from 'models/Listing';
import { COLLEAGUES_INITIAL_VALUE, MONEY_PROPS } from './data';
import { validate } from './utils';
import { PROFILE_PERSONAL_DETAILS_ROUTE } from 'data/global';
import { INDUSTRY } from 'models/Industry';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { DealCreationInitiatedEventProps } from 'models/MixpanelEvents';

import './CreateDealRoom.styles.scss';

const STEPS = {
  0: FirstStep,
  1: SecondStep,
};

type CreateDealRoomProps = {
  className?: string;
};

const CreateDealRoom: React.FC<CreateDealRoomProps> = (props) => {
  const { className } = props;

  const [dealRoomId, setDealRoomId] = useState('');
  const [currentStep, setCurrentStep] = useState(0);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    isUserVerified,
    hasEnterprise,
    enterprise,
    isRWCMember,
    currentUser: { email, firstName, lastName, companyName },
    openVerificationModal,
  } = useContext(CurrentUserContext);

  const {
    params: { isUrgent, package: listingPackage },
  } = useQueryParams();

  const getMixpanelProps = useGetMixpanelProps();

  const totalSteps = Object.values(STEPS).length;

  const classes = classNames('create-deal-room', className);

  const initialValues = useMemo(
    () => ({
      dealOutcome: DEAL_OUTCOME.SALE_CAMPAIGN,
      priceGuide: ASKING_PRICE_SELECT.FIXED_PRICE,
      totalPackageGST: DEAL_ROOM_TOTAL_PACKAGE_GST.INCLUSIVE,
      stockAtValue: STOCK_AT_VALUE.EXCLUDE_STOCK_AT_VALUE,
      revenueShares: shouldShowFrachisorFee(hasEnterprise) ? [] : [{}],
      companyName: enterprise?.brandName || companyName || '',
      colleagues: [
        {
          ...COLLEAGUES_INITIAL_VALUE,
          isOwner: true,
          name: getUserFullName(firstName, lastName),
          email,
        },
      ],
    }),
    [
      companyName,
      email,
      enterprise?.brandName,
      firstName,
      hasEnterprise,
      lastName,
    ],
  );

  const RenderStep = useMemo(
    () => STEPS[currentStep as keyof typeof STEPS],
    [currentStep],
  );

  const navigate = useNavigate();

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (values: PublishDealRoomValues) => {
      if (currentStep < totalSteps - 1 && !hasEnterprise) {
        setCurrentStep((prev) => prev + 1);
        return;
      }

      const {
        createListing,
        uploadListingIM,
        listingIM,
        colleagues,
        revenueShares,
        companyName,
        franchisorFeePercentage,
        industry,
        name,
        ...restValues
      } = values;

      const hasRevenueShares =
        revenueShares?.length &&
        getShouldHasRevenueShares(
          values.projectedSalePrice,
          values.successFeePercentage,
        );

      const newValues = {
        ...restValues,
        name,
        industry: industry || INDUSTRY.OTHER,
        companyName: companyName || null,
        ...(hasRevenueShares && {
          franchisorFeePercentage,
          revenueShares: revenueShares
            .filter(({ orderNumber, ...rest }) => Object.values(rest).length)
            .map(
              ({ percentage, description, label, email, ...rest }, index) => ({
                ...rest,
                description,
                orderNumber: index,
                ...(description === REVENUE_DESCRIPTION.BROKERS_COMMISSION &&
                  email && { email }),
                ...(description === REVENUE_DESCRIPTION.ADD_CUSTOM && {
                  label,
                }),
                ...(!!percentage && { percentage: +percentage }),
              }),
            ),
        }),
        ...utils.transformCurrencyPropertiesToNumber(restValues, MONEY_PROPS),
        colleagues: colleagues.map(({ isOwner, name, ...rest }) => rest),
      };

      try {
        mixpanelService.dealRoomCreationInitiated(
          getMixpanelProps({
            dealId: dealRoomId,
            dealName: name,
          }) as DealCreationInitiatedEventProps,
        );
        const {
          data: {
            dataRoomId,
            listing: { type, _id },
          },
        } = await api.dealRoom.publishDeal(dealRoomId, newValues);

        const draftListing =
          createListing === CREATE_LISTING_OPTIONS.DRAFT_LISTING;

        const dealHasIm = draftListing && uploadListingIM === YesNo.Yes;

        navigate(
          dealHasIm
            ? `./im-upload-successful`
            : draftListing
            ? getCreateListingRoute(dealRoomId, _id, type)
            : dataRoomId && isRWCMember
            ? getDataRoomRoute(dataRoomId)
            : getSellingPortfolio(dealRoomId),
          { replace: true },
        );
      } catch (error) {
        popServerError(error);
      }
    },
    [
      currentStep,
      totalSteps,
      dealRoomId,
      hasEnterprise,
      isRWCMember,
      getMixpanelProps,
      navigate,
      popServerError,
    ],
  );

  useEffect(() => {
    if (!isUserVerified) return;

    (async () => {
      try {
        const intialData = {
          type: isUrgent ? LISTING_URGENT : LISTING_GOING_CONCERN,
          ...(listingPackage ? { listingPackage } : {}),
        };
        const { data } = await api.dealRoom.createInitialDeal(intialData);
        setDealRoomId(data._id);
      } catch (error) {
        sentryError(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isUserVerified) {
      navigate(PROFILE_PERSONAL_DETAILS_ROUTE, { replace: true });
      openVerificationModal(true);
    }
  }, [isUserVerified, navigate, openVerificationModal]);

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => validate(values, hasEnterprise)}
      mutators={{ ...arrayMutators }}
      render={({ submitting, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          <FormWrapper
            title={t('createANewDeal')}
            currentStep={currentStep}
            disabledSubmit={submitting}
            onBack={() => setCurrentStep((prev) => prev - 1)}
          >
            <RenderStep title={t('letsCreateADeal')} dealRoomId={dealRoomId} />
          </FormWrapper>
        </form>
      )}
    />
  );
};

export default CreateDealRoom;
